/**
 * Redux Store Configuration
 * Secure, optimized store setup with proper TypeScript integration
 */

import { configureStore, combineReducers, type Middleware } from '@reduxjs/toolkit';
import type { RootState } from './types';
import { resetStore } from './resetAction';
import { userApi } from './slices/userSlice';
import { productSlice } from './slices/productSlice';
import searchNicheReducer from './slices/searchNicheSlice';
import supplierSearchReducer from './slices/supplierSearchSlice';
import { subscriptionApi } from './slices/subscriptionSlice';
import uiReducer from './slices/uiSlice';
import productResearchReducer from './slices/productResearchSlice';
import nicheReportsReducer from './slices/nicheReportsSlice';
import researchHistoryReducer from './slices/researchHistorySlice';
import { supplierSearchPollingMiddleware } from './middleware/supplierSearchPollingMiddleware';
import { logoutMiddleware } from './middleware/logoutMiddleware';
import { productIdeasApi } from './slices/productIdeasSlice';
import { chatApi } from './slices/chatSlice';
import { productChatApi } from './slices/productChatSlice';
import { onboardingApi } from './slices/onboardingSlice';
import { trendsApi } from './slices/trendsSlice';
import calculatorReducer from './slices/calculatorSlice';
import { calculatorApi } from './slices/calculatorApi';
import tourReducer from './slices/tourSlice';

// Development middleware for debugging (only in development)
const developmentMiddleware: Middleware[] = [];

if (process.env.NODE_ENV === 'development') {
    // Add any development-specific middleware here if needed
}

// Action to reset all store state on logout — defined in resetAction.ts
export { resetStore } from './resetAction';

const combinedReducer = combineReducers({
        products: productSlice.reducer,
        searchNiche: searchNicheReducer,
        supplierSearch: supplierSearchReducer,
        ui: uiReducer,
        productResearch: productResearchReducer,
        nicheReports: nicheReportsReducer,
        researchHistory: researchHistoryReducer,
        calculator: calculatorReducer,
        // RTK Query API reducers
        [userApi.reducerPath]: userApi.reducer,
        [calculatorApi.reducerPath]: calculatorApi.reducer,
        [productIdeasApi.reducerPath]: productIdeasApi.reducer,
        [chatApi.reducerPath]: chatApi.reducer,
        [productChatApi.reducerPath]: productChatApi.reducer,
        [subscriptionApi.reducerPath]: subscriptionApi.reducer,
        [onboardingApi.reducerPath]: onboardingApi.reducer,
        [trendsApi.reducerPath]: trendsApi.reducer,
        tour: tourReducer,
});

const rootReducer = (state: Parameters<typeof combinedReducer>[0], action: any) => {
    if (action.type === resetStore.type) {
        return combinedReducer(undefined, action);
    }
    return combinedReducer(state, action);
};

// Configure the Redux store
export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => {
        const defaultMiddleware = getDefaultMiddleware({
            serializableCheck: {
                // Ignore these action types for serialization check
                ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
                // Ignore these field paths in all actions
                ignoredActionsPaths: ['meta.arg', 'payload.timestamp'],
                // Ignore these paths in the state
                ignoredPaths: ['supplierSearch.polling'],
            },
            immutableCheck: {
                // Ignore these paths for immutability check
                ignoredPaths: ['supplierSearch.polling'],
            },
        });

        return defaultMiddleware.concat(
            userApi.middleware,
            productIdeasApi.middleware,
            chatApi.middleware,
            productChatApi.middleware,
            subscriptionApi.middleware,
            onboardingApi.middleware,
            trendsApi.middleware,
            calculatorApi.middleware,
            ...developmentMiddleware,
            supplierSearchPollingMiddleware,
            logoutMiddleware
        );
    },
    devTools: process.env.NODE_ENV === 'development',
});

// Export types for TypeScript integration
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;

// Type-safe selector hook type
export type AppSelector<T> = (state: RootState) => T;

// Export the store as default
export default store;
